/* eslint-disable camelcase */
import * as pc from 'playcanvas';
import gsap from 'gsap';
import {AssetLoader} from './AssetLoader';
import {noise} from './../noise';

const AudioManager = {
  ambientDrone: {},
  ambientConfig: {},

  fluteDrone: {},
  fluteConfig: {},

  choirDrone: {},
  choirConfig: {},

  bellsDrone: {},
  bellsConfig: {},

  drones: [],

  spotAudio: {},
  spotAudioAssets: [],
  spotAudioInstances: [],
  lastSpotAudioPlayed: -1,
  spotDelay_min: 8,
  spotDelay_max: 16,

  init: function(playcanvasApp) {
    this.playcanvasApp = playcanvasApp;
    this.e_audioContainer = new pc.Entity('e_audioContainer');
    playcanvasApp.root.addChild(this.e_audioContainer);

    this.noise_ambient = {origin: 0.2, currentOrigin: 0.2, counter: pc.math.random(-9999, 9999), rate: 0.1, range: 0.15, currentRange: 0.15, value: 0};
    this.ambientDrone = this.createDroneSource('ambient', this.noise_ambient);

    this.noise_flute = {origin: 0.2, currentOrigin: 0.2, counter: pc.math.random(-9999, 9999), rate: 0.025, range: 0.2, currentRange: 0.2, value: 0};
    this.fluteDrone = this.createDroneSource('flute', this.noise_flute);

    this.noise_choir = {origin: 0.2, currentOrigin: 0.2, counter: pc.math.random(-9999, 9999), rate: 0.025, range: 0.2, currentRange: 0.2, value: 0};
    this.choirDrone = this.createDroneSource('choir', this.noise_choir);

    this.noise_bells = {origin: 0.2, currentOrigin: 0.2, counter: pc.math.random(-9999, 9999), rate: 0.025, range: 0.2, currentRange: 0.2, value: 0};
    this.bellsDrone = this.createDroneSource('bells', this.noise_bells);


    this.spotAudioAssets = [],
    this.spotAudioInstances = [],
    this.spotAudio = this.createSpotSource('master');
    this.spotAudioVolume = 0.2;
    this.spotAudioTimer = null;
    this.muted = false;
  },
  createDroneSource: function(name, noiseObj) {
    const droneObj = {
      name: name,
      noiseObj: noiseObj,
      lastAssetPlayed: -1,
      assets: [],
      instances: [],
      orphans: [],
    };
    const e = new pc.Entity('e_drone_' + name);
    droneObj.entity = e;
    const sound = e.addComponent('sound', {});
    droneObj.sound = sound;

    this.e_audioContainer.addChild(e);
    droneObj.slot = sound.addSlot('slot', {
      overlap: true,
      volume: noiseObj.value,
    });

    this.drones.push(droneObj);
    return droneObj;
  },
  createSpotSource: function(name) {
    const e = new pc.Entity('e_spot_' + name);
    const sound = e.addComponent('sound', {});

    this.e_audioContainer.addChild(e);
    const slot = sound.addSlot('spotSoundSlot', {
      overlap: true,
      volume: 0.2,
    });
    return slot;
  },

  addSceneAudioToQueue: function(plantID) {
    const prefix = AudioManager.Config.audioFolder + plantID + '/';
    for (let i = 1; i <= 3; i++) {
      const file_ambient = plantID + '_ambience' + i + '.mp3';
      const file_bells = plantID + '_bells' + i + '.mp3';
      const file_choir = plantID + '_choir' + i + '.mp3';
      const file_flute = plantID + '_flute' + i + '.mp3';

      AssetLoader.queueAudio(prefix + file_ambient);
      AssetLoader.queueAudio(prefix + file_bells);
      AssetLoader.queueAudio(prefix + file_choir);
      AssetLoader.queueAudio(prefix + file_flute);
    }

    // spot bells
    const spotAudioConfig = this.SpotAudio[plantID];
    for (let bellIndex = 1; bellIndex <= spotAudioConfig.bells; bellIndex++) {
      AssetLoader.queueAudio(prefix + 'spot/' + plantID + '_spot_bell_' + bellIndex + '.mp3');
    }
    for (let vocalIndex = 1; vocalIndex <= spotAudioConfig.vocals; vocalIndex++) {
      AssetLoader.queueAudio(prefix + 'spot/' + plantID + '_spot_vocal_' + vocalIndex + '.mp3');
    }
  },
  setupDrone(drone, assetNames) {
    const assetList = [];
    assetNames.forEach((assetName) => {
      assetList.push(AssetLoader.audioAssets[assetName]);
    });
    drone.assets = assetList;
  },
  playDrone(drone) {
    if (!this.muted) {
      let assetIndex = drone.lastAssetPlayed;
      while (assetIndex === drone.lastAssetPlayed) {
        assetIndex = Math.floor(Math.random() * 3);
      }
      drone.slot.asset = drone.assets[assetIndex];
      drone.lastAssetPlayed = assetIndex;
      drone.instances.push(drone.slot.play());
      drone.timer = gsap.delayedCall(6, AudioManager.playDrone, [drone]);
    }
  },
  cancelDrone(drone, fadeDuration = 6) {
    if (drone.timer) {
      drone.timer.kill();
    }
    drone.instances = [];
    drone.orphans = [];
    drone.slot.instances.forEach((soundInstance) => {
      drone.orphans.push(soundInstance);
      soundInstance.volume = drone.noiseObj.value;
      gsap.to(soundInstance, {volume: 0, duration: fadeDuration});
    });
  },
  cancelDrones() {
    this.drones.forEach((drone) => {
      this.cancelDrone(drone);
    });
  },
  startSpotAudioTimer: function() {
    if (this.spotAudioTimer != null) {
      this.spotAudioTimer.kill();
    }
    this.spotAudioTimer = gsap.delayedCall(pc.math.random(this.spotDelay_min, this.spotDelay_max), () => this.playSpotAudio());
  },
  playSpotAudio() {
    if (!this.muted) {
      let assetIndex = this.lastSpotAudioPlayed;
      while (assetIndex === this.lastSpotAudioPlayed) {
        assetIndex = Math.floor(Math.random() * this.spotAudioAssets.length);
      }
      this.spotAudio.asset = AssetLoader.audioAssets[this.spotAudioAssets[assetIndex]];
      this.lastSpotAudioPlayed = assetIndex;
      this.spotAudioInstances.push(this.spotAudio.play());
      this.startSpotAudioTimer();
    }
  },
  setDroneVolume: function(drone, volume) {
    drone.instances.forEach((soundInstance) => {
      if (soundInstance?.volume != null) {
        soundInstance.volume = volume;
      }
    });
  },
  setSpotVolume: function(volume) {
    this.spotAudioVolume = volume;
    this.spotAudioInstances.forEach((soundInstance) => {
      if (soundInstance?.volume != null) {
        soundInstance.volume = volume;
      }
    });
  },
  fadeTo(drone, volume, range, duration) {
    gsap.killTweensOf(drone.noiseObj, 'origin, range');
    gsap.to(drone.noiseObj, {origin: volume, range: range, duration: duration});
  },
  update: function(dt) {
    if (!this.muted) {
      this.drones.forEach((drone) => {
        const v = this.tickNoise(drone.noiseObj, dt);
        this.setDroneVolume(drone, v);
      });
    }
  },
  tickNoise: function(noiseObj, dt) {
    noiseObj.counter += dt * noiseObj.rate;
    noiseObj.value = noiseObj.origin + noise.simplex2(noiseObj.counter, 0) * (noiseObj.currentRange);
    return noiseObj.value;
  },
  playPlantScene: function(plantID) {
    const ambientAssets = [];
    const choirAssets = [];
    const fluteAssets = [];
    const bellAssets = [];

    for (let i = 1; i <= 3; i++) {
      ambientAssets.push(plantID + '_ambience' + i + '.mp3');
      choirAssets.push(plantID + '_choir' + i + '.mp3');
      fluteAssets.push(plantID + '_flute' + i + '.mp3');
      bellAssets.push(plantID + '_bells' + i + '.mp3');
    }

    this.setupDrone(this.ambientDrone, ambientAssets);
    this.setupDrone(this.choirDrone, choirAssets);
    this.setupDrone(this.fluteDrone, fluteAssets);
    this.setupDrone(this.bellsDrone, bellAssets);

    this.cancelDrone(this.ambientDrone);
    this.playDrone(this.ambientDrone);

    this.cancelDrone(this.choirDrone);
    this.playDrone(this.choirDrone);

    this.cancelDrone(this.fluteDrone);
    this.playDrone(this.fluteDrone);

    this.cancelDrone(this.bellsDrone);
    this.playDrone(this.bellsDrone);

    this.spotAudioAssets = [];
    const spotAudioConfig = AudioManager.SpotAudio[plantID];
    for (let i = 0; i < spotAudioConfig.bells; i++) {
      this.spotAudioAssets.push(plantID + '_spot_bell_' + i + '.mp3');
    }
    for (let j = 0; j < spotAudioConfig.vocals; j++) {
      this.spotAudioAssets.push(plantID + '_spot_vocal_' + j + '.mp3');
    }

    this.startSpotAudioTimer();
  },

  ToggleMute: function() {
    this.muted = !this.muted;
    this.applyMuteState();
  },
  setMute: function(state) {
    this.muted = state;
    this.applyMuteState();
  },
  applyMuteState: function() {
    if (this.muted) {
      this.noise_ambient.currentRange = 0, this.noise_ambient.currentOrigin = 0;
      this.noise_bells.currentRange = 0, this.noise_bells.currentOrigin = 0;
      this.noise_choir.currentRange = 0, this.noise_choir.currentOrigin = 0;
      this.noise_flute.currentRange = 0, this.noise_flute.currentOrigin = 0;

      this.setDroneVolume(this.ambientDrone, 0);
      this.setDroneVolume(this.bellsDrone, 0);
      this.setDroneVolume(this.choirDrone, 0);
      this.setDroneVolume(this.fluteDrone, 0);

      this.setSpotVolume(0);
    } else {
      // not muted - back to normal volume
      this.noise_ambient.currentRange = 0.15, this.noise_ambient.currentOrigin = 0.2;
      this.noise_bells.currentRange = 0.2, this.noise_bells.currentOrigin = 0.2;
      this.noise_choir.currentRange = 0.2, this.noise_choir.currentOrigin = 0.2;
      this.noise_flute.currentRange = 0.2, this.noise_flute.currentOrigin = 0.2;

      this.setDroneVolume(this.ambientDrone, 0.2);
      this.setDroneVolume(this.bellsDrone, 0.2);
      this.setDroneVolume(this.choirDrone, 0.2);
      this.setDroneVolume(this.fluteDrone, 0.2);

      this.setSpotVolume(0.2);
    }
  },
};

AudioManager.Config = {
  audioFolder: './audio/',

  _1_ANTHER: {
    id: '_1_ANTHER',
    spotFiles: [],
  },
};

AudioManager.randomBell = function(plantID) {
  const r = Math.floor(Math.random() * this.SpotAudio[plantID].bells);
  return AssetLoader.audioAssets[plantID + '_spot_bell_' + r];
};
AudioManager.randomVocal = function(plantID) {
  const r = Math.floor(Math.random() * this.SpotAudio[plantID].vocals);
  return AssetLoader.audioAssets[plantID + '_spot_vocal_' + r];
};
AudioManager.SpotAudio = {
  _1_ANTHER: {bells: 11, vocals: 5},
  _2_CONVO: {bells: 7, vocals: 7},
  _3_TRICHO: {bells: 7, vocals: 7},
  _4_ANA: {bells: 9, vocals: 9},
  _5_PETROR: {bells: 9, vocals: 9},
  _6_HYPO: {bells: 7, vocals: 7},
  _7_MALVA: {bells: 9, vocals: 9},
  _8_NYMPH: {bells: 8, vocals: 7},
  _9_MESEM: {bells: 9, vocals: 9},
  _10_CALEN: {bells: 12, vocals: 12},
  _11_HIERA: {bells: 10, vocals: 10},
  _12_HEMERO: {bells: 10, vocals: 10},
  _13_PAPA: {bells: 3, vocals: 4},
};

export {AudioManager};
